import React, { lazy } from 'react';
import { Route } from 'react-router-dom-v5-compat';
import ConfigProvider from 'antd/es/config-provider';

import { OpApp } from 'new-components/DLS/OpApp/OpApp';
import { OpAppScaffold } from 'new-components/OpAppScaffold/OpAppScaffold';
import { OpAuthenticatedRoute } from 'new-components/OpAppScaffold/components/OpAuthenticatedRoute';
import { OrgIdOutlet } from 'new-components/OpAppScaffold/components/OrgIdOutlet';
import { OpScopeGuard } from 'new-components/OpScopeGuard/OpScopeGuard';
import { OpWithoutSidebar } from 'new-components/OpAppScaffold/components/OpWithoutSidebar';
import { getBasenameForApp } from 'utils/getBasenameForApp';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useThemeObserver } from 'utils/customHooks/useThemeObserver';
import { useSelectorJs } from 'utils/customHooks';
import { getItemConfig } from 'config/utils';
import { Loader } from 'components/Loader';
import { AlertWrapper } from 'containers/AlertWrapper/AlertWrapper';
import NotFoundPage from 'pages/NotFoundPage/NotFoundPage';
import { OpTermsGuard } from 'new-components/OpAppScaffold/components/OpTermsGuard';
import { getControlCenterOrigin } from 'utils/getControlCenterOrigin';
import { tableStateKeys } from 'new-components/DLS/OpTableCore/constants/tableStateKeys';
import { getOpParams } from 'utils/getOpParams';
import { requiredTermsRoute } from 'routes/constants';

import {
  baseRoute,
  visitorRoutes,
  visitorTrialEndRoute,
  visitorWelcomeRoute,
} from './constants/routes';
import { TrialWarningBanner } from './components/TrialWarningBanner/TrialWarningBanner';
import ActionFloatButtons from './components/layout/pageContent/ActionFloatButtons/ActionFloatButtons';
import TrialEnd from './pages/TrialEnd/TrialEnd';
import {
  sidebarItems,
  dashboardMenuItem,
  visitorsMenuItem,
  locationsMenuItem,
  kiosksMenuItem,
  redFlagMenuItem,
} from './constants/sidebarItems';
import { ImmutableRootState } from './store/store';
import { IntegrationState } from './store/slices/integrationSlice';
import { VisitorWelcomePageGuard } from './VisitorWelcomePageGuard';
import BulkSchedule from './pages/visitors/BulkSchedule';
import GuestPassReport from './pages/reports/guestPassReport/GuestPassReport';
import GuestPassLog from './pages/reports/guestPassLog/GuestPassLog';
import Welcome from './pages/welcome/Welcome';
import { VisitorDataLoader } from './VisitorDataLoader';
import { NavigateToFirstAvailablePage } from './NavigateToFirstAvailablePage';

import './App.scss';

const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Visitors = lazy(() => import('./pages/visitors/Visitors'));
const Locations = lazy(() => import('./pages/locations/Locations'));
const Kiosks = lazy(() => import('./pages/kiosks/Kiosks'));
const RedFlag = lazy(() => import('./pages/redFlag/RedFlags'));
const VisitorReport = lazy(
  () => import('./pages/reports/visitorReport/VisitorReport'),
);
const VisitorsHourlyReport = lazy(
  () => import('./pages/reports/visitorsHourlyReport/VisitorsHourlyReport'),
);
const RepeatVisitors = lazy(
  () => import('./pages/reports/repeatVisitors/RepeatVisitors'),
);
const RedFlagReport = lazy(
  () => import('./pages/reports/redFlagReport/RedFlagReport'),
);
const HostReport = lazy(() => import('./pages/reports/hostReport/HostReport'));
const ActivityLog = lazy(
  () => import('./pages/reports/activityLog/ActivityLog'),
);
const Invitations = lazy(
  () => import('./pages/configuration/invitations/Invitations'),
);
const Workflow = lazy(() => import('./pages/configuration/workflow/Workflow'));
const FormFields = lazy(
  () => import('./pages/configuration/formFields/FormFields'),
);
const Agreements = lazy(() => import('./pages/agreements/Agreements'));
const Users = lazy(() => import('./pages/users/Users'));
const EmailTemplates = lazy(
  () => import('./pages/configuration/emailTemplates/EmailTemplates'),
);
const RolesPage = lazy(() => import('pages/RolesPage/RolesPage'));
const AvigilonReaders = lazy(
  () => import('./pages/configuration/avigilonReaders/AvigilonReaders'),
);
const Integration = lazy(
  () => import('./pages/administration/integration/Integration'),
);
const VisitorSearchResults = lazy(
  () => import('./pages/visitorSearchResults/VisitorSearchResults'),
);
const Mobile = lazy(() => import('./pages/mobile/mobile'));
const GuestPass = lazy(() => import('./components/guestPass/GuestPass'));
const Register = lazy(() => import('./components/register/Register'));
const RegisterCompleted = lazy(
  () => import('./components/register/RegisterCompleted'),
);
const SelfSignIn = lazy(() => import('./components/selfSignIn/SelfSignIn'));

export const VisitorAppScaffold = () => {
  const basename = getBasenameForApp('visitor');
  const { identityId, isLoading } = useIdentityAndUser();
  const { theme } = useThemeObserver({ identityId });

  const { orgId } = getOpParams();
  const accessTermsUrl = `${getControlCenterOrigin()}/o/${orgId}/${requiredTermsRoute}`;

  const { isMsiIntegrated, isGuestPassIntegrated, isTokenValid } =
    useSelectorJs((state: ImmutableRootState) =>
      state.get('integration'),
    ) as IntegrationState;

  const integrations = { isMsiIntegrated, isGuestPassIntegrated, isTokenValid };
  const allMenuItems = sidebarItems(integrations);
  const pathname = window.location.pathname.toLowerCase();
  const isNoLayoutRoute = [
    '/nl/register',
    '/nl/register/completed',
    '/nl/guestpass',
    '/nl/selfsignin',
  ].some((path) => pathname.includes(path));

  if (!isNoLayoutRoute && isLoading) {
    return <Loader />;
  }

  return (
    <ConfigProvider theme={theme}>
      <OpApp className="op-visitor-app">
        <AlertWrapper />
        <OpAppScaffold
          appScopePrefix="visitor"
          basename={basename}
          homePath={`${baseRoute}/${visitorRoutes.dashboard}`}
          sidebarItems={allMenuItems}
          title="Visitor"
          orgSwitcherProps={{ visible: true }}
          banners={<TrialWarningBanner />}
          footer={<ActionFloatButtons />}
          noLayoutRoutes={
            <>
              <Route path="guestPass" element={<GuestPass />} />
              <Route path="guestPass/:token" element={<GuestPass />} />
              <Route path="register" element={<Register />} />
              <Route
                path="register/completed"
                element={<RegisterCompleted />}
              />
              <Route path="selfSignIn" element={<SelfSignIn />} />
            </>
          }
        >
          <Route
            path={`${baseRoute}/`}
            element={<OrgIdOutlet homePage={visitorRoutes.dashboard} />}
          >
            <Route element={<OpAuthenticatedRoute />}>
              <Route
                path={visitorWelcomeRoute}
                element={
                  <OpWithoutSidebar>
                    <Welcome />
                  </OpWithoutSidebar>
                }
              />
              <Route
                path={visitorTrialEndRoute}
                element={
                  <OpWithoutSidebar>
                    <TrialEnd />
                  </OpWithoutSidebar>
                }
              />
              <Route element={<VisitorWelcomePageGuard />}>
                <Route
                  element={<OpTermsGuard url={accessTermsUrl} isExternal />}
                >
                  <Route element={<VisitorDataLoader />}>
                    {/* Main Menu Routes */}
                    <Route
                      path={visitorRoutes.dashboard}
                      element={
                        <OpScopeGuard
                          allowedScopes={dashboardMenuItem().scope ?? []}
                          element={<Dashboard />}
                          fallback={<NavigateToFirstAvailablePage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.visitors}
                      element={
                        <OpScopeGuard
                          allowedScopes={visitorsMenuItem().scope ?? []}
                          element={<Visitors />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.bulkSchedule}
                      element={
                        <OpScopeGuard
                          allowedScopes={visitorsMenuItem().scope ?? []}
                          element={<BulkSchedule />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.locations}
                      element={
                        <OpScopeGuard
                          allowedScopes={locationsMenuItem().scope ?? []}
                          element={<Locations />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.kiosks}
                      element={
                        <OpScopeGuard
                          allowedScopes={kiosksMenuItem().scope ?? []}
                          element={<Kiosks />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.redFlag}
                      element={
                        <OpScopeGuard
                          allowedScopes={redFlagMenuItem().scope ?? []}
                          element={<RedFlag />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />

                    {/* Reports Routes */}
                    <Route
                      path={visitorRoutes.reports.visitorReport}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.reports.visitorReport,
                            )?.scope ?? []
                          }
                          element={<VisitorReport />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.reports.visitorsHourlyReport}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.reports.visitorsHourlyReport,
                            )?.scope ?? []
                          }
                          element={<VisitorsHourlyReport />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.reports.repeatVisitors}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.reports.repeatVisitors,
                            )?.scope ?? []
                          }
                          element={<RepeatVisitors />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.reports.redFlagReport}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.reports.redFlagReport,
                            )?.scope ?? []
                          }
                          element={<RedFlagReport />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.reports.hostReport}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.reports.hostReport,
                            )?.scope ?? []
                          }
                          element={<HostReport />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.reports.guestPassReport}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.reports.guestPassReport,
                            )?.scope ?? []
                          }
                          element={<GuestPassReport />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.reports.guestPassLog}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.reports.guestPassLog,
                            )?.scope ?? []
                          }
                          element={<GuestPassLog />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.reports.activityLog}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.reports.activityLog,
                            )?.scope ?? []
                          }
                          element={<ActivityLog />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />

                    {/* Configuration & Administration Routes */}
                    <Route
                      path={visitorRoutes.configuration.invitations}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.configuration.invitations,
                            )?.scope ?? []
                          }
                          element={<Invitations />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.configuration.workflow}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.configuration.workflow,
                            )?.scope ?? []
                          }
                          element={<Workflow />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.configuration.formFields}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.configuration.formFields,
                            )?.scope ?? []
                          }
                          element={<FormFields />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.configuration.agreements}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.configuration.agreements,
                            )?.scope ?? []
                          }
                          element={<Agreements />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.configuration.users}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.configuration.users,
                            )?.scope ?? []
                          }
                          element={<Users />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.configuration.userRoles}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.configuration.userRoles,
                            )?.scope ?? []
                          }
                          element={
                            <RolesPage
                              uiStateKey={tableStateKeys.VisitorRolesTable}
                              defaultFilters={{ name: ['Visitor'] }}
                              gtm="visitor-roles"
                            />
                          }
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.configuration.emailTemplates}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.configuration.emailTemplates,
                            )?.scope ?? []
                          }
                          element={<EmailTemplates />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.configuration.avigilonReaders}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.configuration.avigilonReaders,
                            )?.scope ?? []
                          }
                          element={<AvigilonReaders />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />

                    {/* Administration */}
                    <Route
                      path={visitorRoutes.administration.integration}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.administration.integration,
                            )?.scope ?? []
                          }
                          element={<Integration />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    <Route
                      path={visitorRoutes.visitorSearchResults}
                      element={
                        <OpScopeGuard
                          allowedScopes={
                            getItemConfig(
                              allMenuItems,
                              visitorRoutes.visitorSearchResults,
                            )?.scope ?? []
                          }
                          element={<VisitorSearchResults />}
                          fallback={<NotFoundPage />}
                        />
                      }
                    />
                    {/* Mobile */}
                    <Route path={visitorRoutes.mobile} element={<Mobile />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
        </OpAppScaffold>
      </OpApp>
    </ConfigProvider>
  );
};
